import { inject, Injectable } from '@angular/core';
import { DefaultUrlSerializer, Params, UrlSerializer, UrlTree } from '@angular/router';
import { CryptoService } from './crypto.service';

export const ENCRYPTED_QUERY_KEY = 'q';

@Injectable()
export class EncryptedUrlSerializer implements UrlSerializer {
  private readonly base = new DefaultUrlSerializer();
  private readonly crypto = inject(CryptoService);

  parse(url: string): UrlTree {
    const tree = this.base.parse(url);
    const token = tree.queryParams[ENCRYPTED_QUERY_KEY];

    if (!token) {
      return tree;
    }

    const { [ENCRYPTED_QUERY_KEY]: _omit, ...rest } = tree.queryParams;

    try {
      const decrypted = this.crypto.decryptCompact(token);
      tree.queryParams =
        decrypted && typeof decrypted === 'object' ? { ...rest, ...decrypted } : rest;
    } catch {
      tree.queryParams = rest;
    }

    return tree;
  }

  serialize(tree: UrlTree): string {
    const params: Params = tree.queryParams ?? {};
    const keys = Object.keys(params);
    const alreadyEncrypted = keys.length === 1 && keys[0] === ENCRYPTED_QUERY_KEY;

    if (keys.length === 0 || alreadyEncrypted) {
      return this.base.serialize(tree);
    }

    try {
      const encryptedTree = this.base.parse(this.base.serialize(tree));
      encryptedTree.queryParams = {
        [ENCRYPTED_QUERY_KEY]: this.crypto.encryptCompact(params),
      };
      return this.base.serialize(encryptedTree);
    } catch {
      return this.base.serialize(tree);
    }
  }
}
